import { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { omit, isEqual } from 'lodash'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'

import { calcChartProps } from '@aytm/app/stats/page/question-views/oex/lib/props/chart'
import {
  buildAnswerVariantProps,
  calcAnswerVariantsProps
} from '@aytm/app/stats/page/question-views/answer-variants/lib/props/variants'
import { calcTableProps, calcCommentsClick } from '@aytm/app/stats/page/question-views/answer-variants/lib/table-utils'

import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

import { calcControlsProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/controls'
import { calcWordCloudProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/word-cloud'

import { calcChartPlaceholderHeight } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/placeholder'

import useAnswerVariantsState from '@aytm/app/stats/page/question-views/answer-variants/states/useAnswerVariantsState'

import Items, { answerVariantItems } from '@aytm/app/stats/page/question-views/oex/components/Items'
import Chart from '@aytm/app/stats/page/question-views/answer-variants/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Controls from '@aytm/app/stats/page/question-views/components/controls/Controls'
import { BlockContent as OeBlock } from '@aytm/app/stats/page/question-views/oe/Block'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// The answer-variants block -- what a radio-button or checkbox question draws. A strip of controls,
// then either a chart over the variants or the word cloud they make between them, then the variants
// as a table, then the expandable list where a variant with verbatims draws its own open-ended block.
//
// block.coffee owns the mount, the report-param write and the payload. Everything the researcher
// sees and moves is here, and the block answers for its whole subtree through the api it hands up.
const Block = props => {
  const {
    id,
    questionId,
    link,
    isReportXpert,
    questionKey,
    type,
    preType,
    format,
    components,
    answer,
    answerVariants,
    answerType,
    clickable,
    questionChartMode,
    globalChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    labelTarget,
    handlers,
    onReady,
    onStateChange
  } = props

  const {
    chartMode,
    zoomLevel,
    sortOrder,
    uncollapsed,
    isWordCloud,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onWcReady,
    onWcToggle,
    onAnswerVariantReady,
    onAnswerVariantStateChange,
    onAnswerVariantWcParams,
    loadingRows,
    onToggle,
    onToggleAll
  } = useAnswerVariantsState({
    params: props.params,
    paramsRevision: props.paramsRevision,
    questionKey,
    type,
    format,
    answer,
    answerVariants,
    questionChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    labelTarget,
    onReady,
    onStateChange,
    onAnswerVariantParamsChange: props.onAnswerVariantParamsChange,
    onAnswerVariantWcParamsChange: props.onAnswerVariantWcParamsChange,
    onUpdateData: props.onUpdateData
  })

  // The props an open variant's block draws from. It is a question of its own as far as the report
  // params go, so it announces its own changes and this block forwards them to the shell.
  const oePropsOf = useMemo(
    () =>
      buildAnswerVariantProps({
        questionId: id,
        questionKey,
        type,
        format,
        components,
        answer,
        modelDefaults: props.modelDefaults,
        clickable,
        questionChartMode,
        defaultSortOrder,
        reportPercentDecimals,
        batch: labelTarget?.batch,
        link: props.link,
        isReportXpert: props.isReportXpert,
        answerVariantParams: props.answerVariantParams,
        loadingRows,
        handlers: {
          ...handlers,
          onAnswerVariantReady,
          onAnswerVariantStateChange,
          onAnswerVariantWcParamsChange: onAnswerVariantWcParams
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      questionKey,
      type,
      format,
      components,
      answer,
      clickable,
      questionChartMode,
      defaultSortOrder,
      reportPercentDecimals,
      labelTarget,
      handlers,
      onAnswerVariantReady,
      onAnswerVariantStateChange,
      onAnswerVariantWcParams,
      props.modelDefaults,
      props.link,
      props.isReportXpert,
      props.answerVariantParams,
      loadingRows
    ]
  )

  const chartHandlers = useMemo(
    () => ({
      ...handlers,
      onChartModeChange,
      onZoomChange,
      // the chart hands its api to both sides: the shell serializes the svg, the state reads the axis
      onChartReady: api => {
        onChartReady(api)
        handlers?.onChartReady?.(api)
      }
    }),
    [handlers, onChartModeChange, onZoomChange, onChartReady]
  )

  // a clicked column names the order it wants; the state is what settles on one
  const tableHandlers = useMemo(
    () => ({
      ...handlers,
      onCommentsClick: calcCommentsClick({ id, questionId, link, isReportXpert, answerVariants }),
      onSort: (colIndex, dir, cols) => {
        const order = calcSortOrder(cols, colIndex, dir)
        if (order) onSortChange(order)
      }
    }),
    [handlers, id, questionId, link, isReportXpert, answerVariants, onSortChange]
  )

  // the chart draws no image, so it gets the variants without one and keeps them while nothing it
  // draws has changed -- an image change redraws the table alone
  const chartVariantsRef = useRef(null)
  const chartAnswerVariants = useMemo(() => {
    const next = answerVariants?.map(variant => omit(variant, 'image'))
    if (isEqual(next, chartVariantsRef.current)) return chartVariantsRef.current
    chartVariantsRef.current = next
    return next
  }, [answerVariants])

  const question = { ...props, chartMode, zoomLevel, sortOrder, uncollapsed }

  const chartProps = calcChartProps({ ...question, answerVariants: chartAnswerVariants, handlers: chartHandlers })

  // how tall the frame is held open until the chart is scrolled to; a mode that draws no chart at
  // all -- a map, a word cloud -- has no frame to hold open and answers null
  const chartHeight = calcChartPlaceholderHeight({
    ...chartProps,
    itemCount: (chartProps.batches || chartProps.answerVariants)?.length || 0
  })

  // whether the report draws a chart at all here, read once so the list below it agrees
  const chartOn = !isMapOrNone(chartMode, globalChartMode)

  // the variants list closes the block, so the response count sits under it rather than under the
  // chart; a block drawing no list carries the count itself. A list switched on that would draw no
  // row is the same case as one switched off -- an empty plate below the table closes nothing, and
  // the count has to come back up here rather than sit under it
  const itemsOn = useMemo(
    () => isComponentOn(components, 'fields') && 0 < answerVariantItems(answerVariants, type).length,
    [components, answerVariants, type]
  )

  return (
    <>
      <BlockWrapper {...props}>
        <Controls {...calcControlsProps({ ...question, handlers: { ...handlers, onChartModeChange: onWcToggle } })} />

        {isWordCloud ? (
          <OeBlock
            {...calcWordCloudProps({
              ...question,
              handlers: { ...handlers, onWcReady },
              onWcParamsChange: props.onWcParamsChange
            })}
          />
        ) : isComponentOn(components, 'chart') && chartOn ? (
          <ChartPlaceholder height={chartHeight} isLoading={'loading' === answer}>
            <Chart {...chartProps} />
          </ChartPlaceholder>
        ) : null}

        {isComponentOn(components, 'table') && (
          <Table
            {...calcTableProps({
              id,
              questionId: id,
              type,
              preType,
              format,
              components,
              answer,
              answerVariants,
              answerType,
              sortOrder,
              clickable,
              // with no chart above it the list is the only place the magnitudes can be read
              bars: !chartOn,
              handlers: tableHandlers
            })}
          />
        )}
      </BlockWrapper>

      {itemsOn && (
        <BlockWrapper answer={answer}>
          <Items
            {...calcAnswerVariantsProps({ ...question, oePropsOf })}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
          />
        </BlockWrapper>
      )}
    </>
  )
}

Block.displayName = 'AnswerVariantsBlock'

// What the shell hands in: the question, the sections of the report params the block owns, the
// params each open variant owns, and the handlers the surfaces report back through.
export const BLOCK_PROP_TYPES = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  questionType: PropTypes.string,
  preType: PropTypes.number,
  format: PropTypes.object,
  components: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  answerType: PropTypes.string,
  answerLabels: PropTypes.array,
  unitLabel: PropTypes.string,
  batches: PropTypes.array,
  aType: PropTypes.string,
  plotLines: PropTypes.array,
  degree: PropTypes.number,
  scaleTmpl: PropTypes.string,
  scalePow: PropTypes.number,
  scaleSteps: PropTypes.number,
  disabled: PropTypes.bool,
  clickable: PropTypes.bool,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  params: PropTypes.shape({
    cm: PropTypes.string,
    z: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    s: PropTypes.string,
    cb: PropTypes.string,
    wc: PropTypes.string
  }),
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  labelTarget: PropTypes.object,
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  modelDefaults: PropTypes.object,
  // the report params every variant owns, keyed by position
  answerVariantParams: PropTypes.object,
  handlers: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onAnswerVariantParamsChange: PropTypes.func,
  onAnswerVariantWcParamsChange: PropTypes.func,
  onWcParamsChange: PropTypes.func,
  onUpdateData: PropTypes.func
}

Block.propTypes = BLOCK_PROP_TYPES

export default Block
